import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { communicationList } from "../../../services/communicationService";
import { toast } from "react-toastify";
import { useSocket } from "../../../providers/SocketContext";
import { useLocation } from "react-router-dom";
import { organizationList } from "../../../services/organizationService";
import Select from "../../form/front/Select";

// Format sentAt date into readable string
const formatMessageDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: "short" });
  } else if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  } else {
    return date.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
};

const MessageList = ({
  filters = {},
  search = "",
  onSelectChat,
  defaultChatId,
  defaultCompanyId,
  selectedCompany,
  onSelectedCompanyChange = () => {},
}) => {
  const [activeMessageId, setActiveMessageId] = useState(null);
  const { session } = useSelector((state) => state?.authProvider);
  const location = useLocation();
  const currentUserId = session?.user?._id;
  // organizationId may not always be present in session
  const organizationIdRaw = session?.user?.organization;
  const organizationId =
    typeof organizationIdRaw === "string"
      ? organizationIdRaw
      : organizationIdRaw?._id;
  const isCompanyInbox = location.pathname === "/inbox";

  const { data: organizations = [] } = useQuery({
    queryKey: ["organization-list-for-inbox"],
    queryFn: async () => {
      const res = await organizationList({
        limit: 10000,
        ...(currentUserId ? { createdBy: currentUserId } : {}),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs?.data ?? [];
    },
    enabled: isCompanyInbox && !!currentUserId,
  });

  const getId = (val) => (typeof val === "string" ? val : val?._id);

  const organizationsForInbox = (() => {
    if (!isCompanyInbox) return organizations;

    const currentOrgId = getId(organizationId);
    const isMine = (org) => {
      const orgId = getId(org?._id);
      if (currentOrgId && orgId === currentOrgId) return true;

      const createdById = getId(org?.createdBy);
      if (createdById && createdById === currentUserId) return true;

      const ownerId = getId(org?.owner);
      if (ownerId && ownerId === currentUserId) return true;

      const adminLists = [
        org?.admins,
        org?.admin,
        org?.adminUsers,
        org?.members,
        org?.memberData,
        org?.users,
      ].filter(Boolean);

      return adminLists.some((list) => {
        if (!Array.isArray(list)) return false;
        return list.some((m) => getId(m) === currentUserId);
      });
    };

    const mine = organizations.filter(isMine);
    if (mine.length) return mine;

    // Fallback: if we at least know the current org, don't show the whole platform list
    if (currentOrgId) return organizations.filter((o) => getId(o?._id) === currentOrgId);

    return organizations;
  })();

  const uniqueOrganizationsForInbox = Array.from(
    new Map(organizationsForInbox.map((org) => [getId(org?._id), org])).values(),
  ).filter((org) => !!getId(org?._id));

  const companyOptions = uniqueOrganizationsForInbox.map((org) => ({
    value: org?._id,
    name:
      org?.content?.en?.title ||
      org?.name ||
      org?.title ||
      org?.slug ||
      "Company",
  }));

  useEffect(() => {
    if (!isCompanyInbox) return;
    if (!companyOptions.length) return;

    const preferredId =
      defaultCompanyId ||
      selectedCompany?.value ||
      organizationId ||
      companyOptions[0].value;
    const preferred =
      companyOptions.find((c) => c.value === preferredId) || companyOptions[0];

    if (
      !selectedCompany?.value ||
      selectedCompany.value !== preferred.value ||
      !selectedCompany?.name
    ) {
      onSelectedCompanyChange(preferred);
    }
  }, [
    isCompanyInbox,
    defaultCompanyId,
    organizationId,
    companyOptions.length,
    selectedCompany?.value,
    selectedCompany?.name,
  ]);

  const companyIdForInbox = isCompanyInbox ? selectedCompany?.value : undefined;
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "communication-list",
      isCompanyInbox ? "company" : "personal",
      companyIdForInbox || "none",
      currentUserId || "anon",
    ],
    queryFn: async () => {
      if (isCompanyInbox && !companyIdForInbox) return [];
      const res = await communicationList({
        ...(currentUserId ? { createdBy: currentUserId } : {}),
        ...(isCompanyInbox
          ? { recipientType: "company", companyId: companyIdForInbox }
          : {}),
        page: filters?.page || 1,
        limit: filters?.limit || 20,
        // type: "createdByMe",
        // ...(organizationId && { organization: organizationId }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs?.data ?? [];
    },
    // staleTime: 1000 * 60 * 5, // ✅ 5 min cache
    // refetchOnWindowFocus: false, // ✅ important
    // refetchOnMount: false, // ✅ prevent remount refetch
    enabled: true, // adjust if you need to wait for session
  });
  useSocket("getChatListEventResponse", (res) => {
    console.log(res, "ChatList");
  });
  useEffect(() => {
    if (!data?.length) return;

    // ✅ If chatId from URL → priority
    if (defaultChatId) {
      const found = data.find((item) => item._id === defaultChatId);

      if (found) {
        setActiveMessageId(found._id);
        onSelectChat(found);
        return;
      }
    }

    // ✅ Otherwise → select first chat
    if (!activeMessageId) {
      const firstChat = data[0];
      setActiveMessageId(firstChat._id);
      onSelectChat(firstChat);
    }
  }, [defaultChatId, data]);
  // Given a conversation item, find the "other" member (not current user)
  const getOtherMember = (item) => {
    if (!item?.memberData?.length) return null;
    // If only one member, just show that one
    if (item.memberData.length === 1) return item.memberData[0];
    return (
      item.memberData.find((m) => m._id !== currentUserId) ?? item.memberData[0]
    );
  };

  const getDisplayName = (member) => {
    if (!member) return "Unknown";
    if (member?.content?.en?.title) return member.content.en.title;
    if (member?.name) return member.name;
    return `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim() || "Unknown";
  };

  const getAvatarUrl = (member) => {
    return member?.image?.url || member?.logo?.url || null;
  };

  const getMessagePreview = (item) => {
    const latestText = item?.latestMessage?.message || item?.latestMessage?.content;
    if (!latestText) return "";
    const sender = item.memberData?.find(
      (m) => m._id === item.latestMessage.sentBy,
    );
    const senderName = sender ? sender.firstName : "";
    const isSentByMe = item.latestMessage.sentBy === currentUserId;
    const prefix = isSentByMe ? "You" : senderName;
    return prefix
      ? `${prefix}: ${latestText}`
      : latestText;
  };

  if (isLoading || isFetching) {
    return (
      <div className="w-full bg-white max-h-[calc(100vh-218px)] overflow-auto">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="flex items-center space-x-3 px-3 py-4 border-b border-b-gray-200 animate-pulse"
          >
            <div className="rounded-full w-12 h-12 bg-gray-200 shrink-0" />
            <div className="flex flex-col gap-2 w-full">
              <div className="h-3.5 bg-gray-200 rounded w-1/3" />
              <div className="h-3 bg-gray-100 rounded w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!data?.length) {
    return (
      <div className="w-full bg-white">
        {isCompanyInbox && companyOptions.length > 1 && (
          <div className="p-2 border-b border-gray-200 bg-white">
            <Select
              placeholder={"Select company"}
              dropdownData={companyOptions}
              selectedValue={selectedCompany?.value || null}
              onChange={(val, item) => onSelectedCompanyChange(item)}
              dropdownButtonClass={"h-9 rounded-lg bg-white border border-gray-200"}
              xPlacement="bottom-left"
            />
          </div>
        )}
        <div className="flex items-center justify-center py-16 text-sm text-gray-400">
          No conversations found.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white min-h-[calc(100vh-218px)] max-h-[calc(100vh-218px)] overflow-auto rounded-b-xl">
      {isCompanyInbox && companyOptions.length > 1 && (
        <div className="sticky top-0 z-10 p-2 border-b border-gray-200 bg-white">
          <Select
            placeholder={"Select company"}
            dropdownData={companyOptions}
            selectedValue={selectedCompany?.value || null}
            onChange={(_val, item) => onSelectedCompanyChange(item)}
            dropdownButtonClass={"h-9 rounded-lg bg-white border border-gray-200"}
            xPlacement="bottom-left"
          />
        </div>
      )}
      {data.map((item) => {
        const otherMember = getOtherMember(item);
        const displayName = getDisplayName(otherMember);
        const avatarUrl = getAvatarUrl(otherMember);
        const messagePreview = getMessagePreview(item);
        const date = formatMessageDate(
          item.latestMessage?.sentAt || item.latestMessage?.createdAt,
        );

        // Unread: check if current user's _id is NOT in seenBy
        const seenBy = item.communicationData?.seenBy ?? [];
        const isUnread = !seenBy.some(
          (s) => s.user === currentUserId || s.user?._id === currentUserId,
        );

        return (
          <div
            key={item._id}
            onClick={() => {
              setActiveMessageId(item._id);
              onSelectChat(item); // 👈 important
            }}
            className={`flex items-center justify-between px-3 py-4 border-b border-b-gray-200 cursor-pointer group border-l-4 hover:bg-gray-100 ${
              activeMessageId === item._id
                ? "bg-slate-100 border-emerald-600"
                : "border-transparent"
            }`}
          >
            <div className="flex items-center space-x-3 w-full">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt={displayName}
                  className="rounded-full w-10 h-10 object-cover shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-8 h-8 lg:w-9 lg:h-9 rounded-full bg-palatte flex items-center justify-center text-white font-semibold text-lg shrink-0">
                  {displayName?.charAt(0)?.toUpperCase() || "U"}
                </div>
              )}
              <div className="flex flex-col w-full min-w-0">
                <div className="flex gap-2 items-start justify-between">
                  <p
                    className={`text-gray-700 text-sm  truncate ${
                      isUnread ? "font-semibold" : "font-medium"
                    }`}
                  >
                    {displayName}
                  </p>
                  <span className="text-xs text-gray-800 pt-1 shrink-0 ">
                    {date}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <p
                    className={`text-xs truncate ${
                      isUnread ? "text-gray-800 font-medium" : "text-gray-600"
                    }`}
                  >
                    {messagePreview}
                  </p>
                  {/* {isUnread && (
                    <span className="ml-auto shrink-0 w-2 h-2 rounded-full bg-emerald-600" />
                  )} */}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MessageList;
